"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { DemoButton } from "@/components/demo-button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Container } from "@/components/shared/container";
import { MegaMenuContent } from "@/components/layout/mega-menu-content";
import {
  NAV_ITEMS,
  FEATURED_EBOOK,
  isMegaNavItem,
  isSimpleDropdownNavItem,
  isLinkNavItem,
} from "@/lib/constants";
import { BOOKING_PORTAL_URL } from "@/lib/booking-portal";
import { cn } from "@/lib/utils";

export const MOBILE_SECTIONS = [
  {
    label: "Produkt",
    items: [
      { label: "Lista funkcjonalności", href: "/funkcjonalnosci" },
      { label: "Vetina Booking", href: "/booking" },
      { label: "Znajdź weterynarza", href: BOOKING_PORTAL_URL },
      { label: "Cennik", href: "/#cennik" },
    ],
  },
  {
    label: "Porównaj",
    items: [
      { label: "Wszystkie porównania", href: "/porownaj" },
      { label: "vs. Klinika XP", href: "/porownaj/klinika-xp" },
      { label: "vs. Veterinaro", href: "/porownaj/veterinaro" },
    ],
  },
  {
    label: "Zasoby",
    items: [
      { label: "Blog", href: "/blog" },
      { label: "Akademia Vetina", href: "/akademia" },
      { label: "FAQ", href: "/faq" },
      { label: "Broszura PDF", href: "/downloads/Vetina_Broszura_2026.pdf" },
    ],
  },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  return (
    <>
      <header
        className={cn(
          "relative left-0 right-0 transition-all duration-300",
          mobileOpen
            ? "bg-background border-b border-border z-[110]"
            : scrolled
              ? "bg-background/80 backdrop-blur-xl border-b border-border"
              : "bg-transparent"
        )}
      >
        <Container className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/images/logo.svg"
              alt="Vetina"
              width={140}
              height={38}
              className="h-9 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <NavigationMenu viewport={false} className="hidden md:flex">
            <NavigationMenuList>
              {NAV_ITEMS.main.map((item) => {
                if (isMegaNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label} className="static">
                      <NavigationMenuTrigger className="bg-transparent">
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className="left-auto right-0 md:left-0 md:right-auto">
                        <MegaMenuContent item={item} />
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                }

                if (isSimpleDropdownNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuTrigger className="bg-transparent">
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-44 gap-0.5 p-1.5">
                          {item.children.map((child) => (
                            <li key={child.label}>
                              <NavigationMenuLink asChild>
                                <Link
                                  href={child.href}
                                  className="block rounded-md px-2.5 py-1.5 text-sm hover:bg-accent transition-colors"
                                >
                                  {child.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                }

                if (isLinkNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuLink asChild>
                        <Link
                          href={item.href}
                          className="inline-flex h-9 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                }

                return null;
              })}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Desktop CTA */}
          <div className="hidden md:inline-flex">
            <DemoButton source="navbar_desktop" className="shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-shadow">
              {NAV_ITEMS.cta.label}
            </DemoButton>
          </div>

          {/* Mobile: CTA + Toggle */}
          <div className="flex md:hidden items-center gap-2">
            {!mobileOpen && (
              <DemoButton source="navbar_mobile" size="sm" className="h-8 text-xs px-3 shadow-md shadow-primary/20">
                {NAV_ITEMS.cta.label}
              </DemoButton>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={cn(
                "relative z-[110] flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-accent"
              )}
              aria-label={mobileOpen ? "Zamknij menu" : "Otwórz menu"}
            >
              <AnimatePresence mode="wait">
                {mobileOpen ? (
                  <motion.div
                    key="close"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.12 }}
                  >
                    <X className="h-5 w-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Menu className="h-5 w-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </Container>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[100] md:hidden bg-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <motion.nav
              className="flex flex-col h-full pt-32 pb-8 px-6 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.05 }}
            >
              {/* Top CTA - visible without scrolling */}
              <motion.div
                className="mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <DemoButton
                  source="navbar_mobile_menu"
                  size="lg"
                  className="w-full h-12 text-base shadow-md shadow-primary/20"
                  onBeforeOpen={closeMobile}
                >
                  {NAV_ITEMS.cta.label}
                </DemoButton>
              </motion.div>

              <div className="flex flex-col gap-7">
                {MOBILE_SECTIONS.map((section, sIdx) => (
                  <motion.div
                    key={section.label || sIdx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + sIdx * 0.05 }}
                  >
                    {section.label && (
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        {section.label}
                      </p>
                    )}
                    <div className="flex flex-col">
                      {section.items.map((item) => (
                        <Link
                          key={item.label}
                          href={item.href}
                          onClick={closeMobile}
                          className="text-xl font-medium text-foreground py-2 transition-colors hover:text-primary active:text-primary/70"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                ))}

                {/* Featured ebook */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + MOBILE_SECTIONS.length * 0.05 }}
                >
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Polecane
                  </p>
                  <div className="rounded-lg bg-muted/40 p-4">
                    <p className="text-base font-medium">{FEATURED_EBOOK.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{FEATURED_EBOOK.description}</p>
                    <Link
                      href={FEATURED_EBOOK.href}
                      onClick={closeMobile}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
                    >
                      {FEATURED_EBOOK.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
